import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  RowSelectionState,
  OnChangeFn,
} from '@tanstack/react-table';
import { ChevronRightIcon } from 'assets';
import React, { useState, useMemo } from 'react';

export interface CVComparisonCandidate {
  name: string;
  email?: string;
  key_strengths: string[];
  risks_or_gaps: string[];
  fit_score: number;
  recommendation: string;
}

interface CVComparisonTableProps {
  candidates: CVComparisonCandidate[];
  disclaimer?: string;
  options?: Array<{text: string; value: string; color?: string}>;
  onOptionClick?: (option: string) => void;
  onCandidatesSelected?: (candidates: {full_name: string; email: string}[]) => void;
}

const CANDIDATES_TO_INVITE_KEY = 'cv_analysis_candidates_to_invite';

const columns: ColumnDef<CVComparisonCandidate, unknown>[] = [
  {
    id: 'select',
    header: ({ table }) => (
      <input
        type="checkbox"
        checked={table.getIsAllRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
        className="w-4 h-4 rounded border-[#D0D5DD] text-[#012169] focus:ring-[#012169] cursor-pointer"
      />
    ),
    meta: {
      headerClassName: 'w-12 px-3 py-5',
      cellClassName: 'w-12 px-3 py-4 align-top text-center',
    },
    cell: ({ row }) => (
      <input
        type="checkbox"
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
        className="w-4 h-4 rounded border-[#D0D5DD] text-[#012169] focus:ring-[#012169] cursor-pointer"
      />
    ),
  },
  {
    id: 'name',
    header: 'Candidate Name',
    meta: {
      headerClassName: 'w-[17%] px-3 py-5 text-xs font-semibold text-[#1D2939] text-center tracking-wider',
      cellClassName: 'px-3 py-4 align-top',
    },
    cell: ({ row }) => (
      <div className="text-xs font-medium text-[#344054] break-words">{row.original.name}</div>
    ),
  },
  {
    id: 'strengths',
    header: 'Key Strengths',
    meta: {
      headerClassName: 'px-4 py-5 text-left text-xs font-semibold text-[#1D2939] tracking-wider',
      cellClassName: 'px-4 py-4 align-top',
    },
    cell: ({ row }) => (
      <ul className="list-disc list-inside text-xs text-[#344054] space-y-1">
        {row.original.key_strengths.map((strength, i) => (
          <li key={i} className="leading-relaxed">{strength}</li>
        ))}
      </ul>
    ),
  },
  {
    id: 'risks',
    header: 'Risk/Gaps',
    meta: {
      headerClassName: 'px-4 py-5 text-left text-xs font-semibold text-[#1D2939] tracking-wider',
      cellClassName: 'px-4 py-4 align-top',
    },
    cell: ({ row }) => (
      <ul className="list-disc list-inside text-xs text-[#344054] space-y-1">
        {row.original.risks_or_gaps.map((risk, i) => (
          <li key={i} className="leading-relaxed">{risk}</li>
        ))}
      </ul>
    ),
  },
  {
    id: 'fitScore',
    header: 'Fit Score',
    meta: {
      headerClassName: 'w-[14%] px-3 py-5 text-center text-xs font-semibold text-[#1D2939] tracking-wider',
      cellClassName: 'px-3 py-4 align-top whitespace-nowrap text-center',
    },
    cell: ({ row }) => (
      <span className="px-3 py-1 rounded-full text-[10px] font-light">
        <b className="text-base font-semibold">{row.original.fit_score}</b> /10
      </span>
    ),
  },
  {
    id: 'recommendation',
    header: 'Recommendation',
    meta: {
      headerClassName: 'w-[22%] px-4 py-5 text-left text-xs font-semibold text-[#1D2939] tracking-wider',
      cellClassName: 'px-4 py-4 align-top',
    },
    cell: ({ row }) => (
      <div className="text-xs text-[#344054] leading-relaxed">
        {row.original.recommendation}
      </div>
    ),
  },
];

const CVComparisonTable: React.FC<CVComparisonTableProps> = ({
  candidates,
  disclaimer,
  options,
  onOptionClick,
  onCandidatesSelected,
}) => {
  const [expandedIndex, setExpandedIndex] = useState<number>(0);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = (updater) => {
    setRowSelection((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const selected = Object.keys(next)
        .filter((k) => next[k])
        .map((k) => ({
          full_name: candidates[parseInt(k)].name,
          email: candidates[parseInt(k)].email || '',
        }));
      onCandidatesSelected?.(selected);
      return next;
    });
  };

  const table = useReactTable({
    data: candidates,
    columns,
    state: { rowSelection },
    onRowSelectionChange: handleRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    getRowId: (_, index) => index.toString(),
  });

  const selectedCandidates = useMemo(
    () =>
      Object.keys(rowSelection)
        .filter((k) => rowSelection[k])
        .map((k) => ({
          full_name: candidates[parseInt(k)].name,
          email: candidates[parseInt(k)].email || '',
        })),
    [rowSelection, candidates]
  );

  const selectedCount = selectedCandidates.length;

  const toggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? -1 : index);
  };

  const handleOptionClick = (value: string) => {
    if (value === 'administer_assessment' && selectedCount > 0) {
      localStorage.setItem(CANDIDATES_TO_INVITE_KEY, JSON.stringify(selectedCandidates));
      onCandidatesSelected?.(selectedCandidates);
    }
    onOptionClick?.(value);
  };

  return (
    <div className="w-full my-6">
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto border border-[#EAECF0] rounded-3xl">
        <table className="w-full min-w-[700px] table-fixed divide-y divide-[#EAECF0]">
          <thead className="bg-[#EFF8FF]">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="divide-x divide-[#EAECF0]">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={header.column.columnDef.meta?.headerClassName ?? ''}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-[#EAECF0]">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50 transition-colors divide-x divide-[#EAECF0]">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={cell.column.columnDef.meta?.cellClassName ?? ''}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Accordion View */}
      <div className="md:hidden space-y-4">
        {candidates.map((candidate, index) => (
          <div
            key={index}
            className="bg-white rounded-[12px] border border-gray-200 overflow-hidden"
          >
            {/* Header */}
            <button
              onClick={() => toggleExpand(index)}
              className="!bg-[#EFF8FF] w-full px-6 py-5 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-4">
                <input
                  type="checkbox"
                  checked={rowSelection[index.toString()] ?? false}
                  onChange={() => table.getRow(index.toString())?.toggleSelected()}
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 rounded border-[#D0D5DD] text-[#012169] focus:ring-[#012169] cursor-pointer flex-shrink-0"
                />
                <h3 className="text-xl font-medium text-[#344054]">
                  {candidate.name}
                </h3>
                <span className="px-4 py-1 bg-[#B2DDFF] text-[#344054] rounded-lg text-base font-semibold">
                  {candidate.fit_score}
                </span>
              </div>
              {expandedIndex === index ? (
                <ChevronRightIcon className="w-6 h-6 text-gray-400 -rotate-90 [&>path]:stroke-[#667085] [&>path]:stroke-[1px]" />
              ) : (
                <ChevronRightIcon className="w-6 h-6 text-gray-400 rotate-90 [&>path]:stroke-[#667085] [&>path]:stroke-[1px]" />
              )}
            </button>

            {/* Expandable Content */}
            {expandedIndex === index && (
              <div className="px-6 py-5 bg-gray-50 border-t border-gray-200 space-y-6">
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-3">Key Strengths</h4>
                  <ul className="space-y-2">
                    {candidate.key_strengths.map((strength, i) => (
                      <li key={i} className="flex items-start">
                        <span className="text-gray-700 text-base leading-relaxed">• {strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-3">Risk/Gaps</h4>
                  <ul className="space-y-2">
                    {candidate.risks_or_gaps.map((risk, i) => (
                      <li key={i} className="flex items-start">
                        <span className="text-gray-700 text-base leading-relaxed">• {risk}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-3">Recommendation</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-gray-700 text-base leading-relaxed">• {candidate.recommendation}</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {selectedCount > 0 && (
        <p className="mt-3 text-xs text-[#344054] font-medium">
          {selectedCount} candidate{selectedCount !== 1 ? 's' : ''} selected
        </p>
      )}

      {disclaimer && (
        <div className="mt-6 border-t border-[#D0D5DD] py-4">
          <h4 className="text-sm font-semibold mb-2">Disclaimer:</h4>
          <p className="text-xs whitespace-pre-line leading-relaxed">{disclaimer}</p>
        </div>
      )}

      {options && options.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2">
          {options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleOptionClick(option.value)}
              className={`px-6 py-2.5 rounded-md !ml-0 font-medium text-xs transition-colors ${
                option.color === 'primary' || index === 0
                  ? 'bg-[#012169] text-white hover:bg-[#012169]/80'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {option.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export { CVComparisonTable };
